/**
 * Authenticated Fetch Utility
 *
 * Wraps fetch to include the Auth0 access token as an Authorization: Bearer header.
 *
 * If a token refresh is in flight, requests are held until it completes — preventing
 * stale-token 403s on cold start.
 */

import { useAuthStore } from "../auth-store";
import { getRefreshPromise } from "../auth-store";
import { useImpersonationStore } from "../impersonation-store";
import { ForbiddenError, UnauthorizedError } from "./auth-errors";

export { ForbiddenError, UnauthorizedError } from "./auth-errors";

export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Await any in-flight token refresh before reading the access token.
  // This prevents sending a stale token that would result in a 403.
  const pending = getRefreshPromise();
  if (pending) {
    await pending;
  }

  const store = useAuthStore.getState();

  let accessToken = await store.getAccessToken();

  const makeRequest = (accessToken: string): Promise<Response> => {
    const headers = new Headers(options.headers);
    headers.set("Authorization", `Bearer ${accessToken}`);

    const { impersonatedUserId } = useImpersonationStore.getState();
    if (impersonatedUserId) {
      headers.set("x-a3s-impersonate", impersonatedUserId);
    }

    if (!headers.has("Content-Type") && options.body) {
      headers.set("Content-Type", "application/json");
    }

    return fetch(url, { ...options, headers });
  };

  if (!accessToken) {
    await store.clearAuth();
    throw new UnauthorizedError("No authentication tokens available");
  }

  let response = await makeRequest(accessToken);

  // Handle 401/403 — attempt Auth0 token refresh once. The backend returns 403
  // (not 401) when a token is expired or revoked, so we treat both as "possibly
  // stale token" on the first attempt. If the retry still fails, it's a real error.
  //
  // TODO: The backend token authorizers currently return 500 for malformed tokens
  // instead of 401. Until that's fixed, this retry only catches expired/revoked
  // tokens that are still well-formed JWTs — not corrupted ones.
  if (response.status === 401 || response.status === 403) {
    const refreshed = await store.refreshToken({ force: true });
    if (refreshed) {
      accessToken = await store.getAccessToken();
      if (accessToken) {
        response = await makeRequest(accessToken);
      }
    } else if (!useAuthStore.getState().isAuthenticated) {
      // Refresh failed and clearAuth() was called — session is dead
      throw new UnauthorizedError("Session expired");
    }
    // else: refresh returned false but session intact (e.g. no refresh token
    // stored). Fall through to the status checks below so a real 403 is
    // surfaced as ForbiddenError, not misclassified as session expiry.
  }

  if (response.status === 401) {
    throw new UnauthorizedError("Authentication required");
  }

  if (response.status === 403) {
    throw new ForbiddenError("Access denied - insufficient permissions");
  }

  return response;
}
